// RG 28.08 9.1 (K6), D5: formatting of the target Statement's figures and date
// for the ON-SCREEN statement text. A pure module on purpose: the screen code
// (TargetSection, TargetStatementText, TargetArea) must never import from
// PdfDocument.ts / TargetPdfDocument.ts / pdfStamp.ts / pdfAnchors.ts -- those
// pull in pdf-lib, pdfjs and the .ttf font, and the tests mock them partially,
// so an import would resolve to `undefined` there.
//
// The rules below are deliberate copies of the ones the stamped PDF uses
// (resolvePdfLanguage in PdfDocument.ts, formatDecimal in TargetPdfDocument.ts,
// formatPdfDate in PdfDocument.ts), so the text on screen and the downloaded
// file always show the same numbers and date. Change them together.

export type StatementLanguage = 'pl' | 'en' | 'de';

// The Statement exists in PL/EN/DE -- the remaining UI locales (cs/nl) fall
// back to EN.
export function resolveStatementLanguage(
  uiLanguage: string
): StatementLanguage {
  if (uiLanguage === 'pl') return 'pl';
  if (uiLanguage === 'de') return 'de';
  return 'en';
}

// The Polish and German statements use the comma decimal separator
// (51,4 / 462,84), the English one keeps the dot. Years are integers and
// never go through here.
export function formatStatementDecimal(
  value: number,
  lang: StatementLanguage
): string {
  const text = String(value);
  return lang === 'en' ? text : text.replace('.', ',');
}

// One date per language: 27.08.2026 (pl/de -- de-DE's default drops leading
// zeros, hence the explicit 2-digit options) / 27/08/2026 (en-GB).
//
// RG 28.08 9.1: the preview endpoint sends its date as a bare "2026-10-01".
// `new Date()` reads that as UTC midnight, which a browser west of Greenwich
// would format as the day before -- so a bare date is pinned to midday UTC
// first. Full timestamps (what the stamped PDF gets) are untouched.
export function formatStatementDate(
  lang: StatementLanguage,
  isoDate: string
): string {
  const instant = /^\d{4}-\d{2}-\d{2}$/.test(isoDate)
    ? `${isoDate}T12:00:00Z`
    : isoDate;
  if (lang === 'en') {
    return new Intl.DateTimeFormat('en-GB').format(new Date(instant));
  }
  return new Intl.DateTimeFormat(lang === 'pl' ? 'pl-PL' : 'de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(instant));
}
